import { useState } from 'react';
import { ChevronRight, Loader2, FolderOpen, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field, FieldLabel } from '@/components/ui/field';
import { TabSwitcher } from '@/components/ui/app/tab-switcher';
import { FolderTreeSelector, FolderPathDisplay } from '@/components/ui/app/folder-tree-selector';
import { useTranslation } from 'react-i18next';

/**
 * Étape de sélection du dossier de destination - étape obligatoire avant le formulaire
 * Affichée comme une section du formulaire, pas comme un drawer
 */
export default function StepSelectFolder({
    onSelect,
    isCreatingDraft = false,
    templateName = 'Rapport',
    templateColor = 'var(--secondary)',
}) {
    const { t } = useTranslation();
    const [saveMode, setSaveMode] = useState('new'); // 'new' | 'existing'
    const [newFolderName, setNewFolderName] = useState('');

    // Sélection arborescente : { type, uniq_id, path, name }
    const [selectedDestination, setSelectedDestination] = useState(null);

    const canSubmit = saveMode === 'new' ? newFolderName.trim().length > 0 : !!selectedDestination?.uniq_id;

    const handleSubmit = () => {
        if (!canSubmit || isCreatingDraft) return;

        if (saveMode === 'new') {
            onSelect({ mode: 'new', folderName: newFolderName.trim() });
        } else {
            // Pour un subfolder, on passe le folder parent comme folderId (pour l'URL API)
            // et le subfolder comme parent_subfolder_id
            onSelect({
                mode: 'existing',
                folderId: selectedDestination.folderUniqId, // Toujours le dossier racine
                folderName: selectedDestination.name || '',
                destinationType: selectedDestination.type,
                parentSubfolderId: selectedDestination.type === 'subfolder' ? selectedDestination.uniq_id : null,
            });
        }
    };

    return (
        <div>
            {/* Section Header - même style que les autres sections */}
            <div className="text-center mb-[24px]">
                <div
                    className="w-[48px] h-[48px] rounded-full flex items-center justify-center mx-auto mb-[12px] shadow-sm"
                    style={{ backgroundColor: templateColor }}
                >
                    <FolderOpen className="h-6 w-6 text-primary-foreground" />
                </div>
                <h2 className="text-[20px] text-foreground mb-[6px]">{t('procedures:destinationDuRapport')}</h2>
                <p className="text-[13px] text-muted-foreground">{t('procedures:choisissezOuEnregistrer')}{templateName}"</p>
            </div>

            {/* Form Card */}
            <div className="bg-card rounded-[16px] p-[20px] mb-[20px] border border-border space-y-[20px]">
                {/* Mode Toggle */}
                <TabSwitcher
                    tabs={[
                        { value: 'new', label: t('procedures:nouveauDossier') },
                        { value: 'existing', label: t('procedures:dossierExistant') },
                    ]}
                    activeTab={saveMode}
                    onChange={setSaveMode}
                />

                {/* New Folder Input */}
                {saveMode === 'new' && (
                    <Field>
                        <FieldLabel required>{t('procedures:nomDuNouveauDossier')}</FieldLabel>
                        <Input
                            type="text"
                            value={newFolderName}
                            onChange={(e) => setNewFolderName(e.target.value)}
                            placeholder={t('procedures:exChantierRueDeLaPaix')}
                        />
                    </Field>
                )}

                {/* Existing Folder Selector - Arborescence */}
                {saveMode === 'existing' && (
                    <Field>
                        <FieldLabel required>{t('procedures:selectionnerUnDossierOuSousDossier')}</FieldLabel>

                        {/* Affichage du chemin sélectionné */}
                        {selectedDestination?.path && (
                            <FolderPathDisplay path={selectedDestination.path} className="mb-3" />
                        )}

                        {/* Arborescence des dossiers */}
                        <FolderTreeSelector
                            value={selectedDestination?.uniq_id}
                            onChange={setSelectedDestination}
                            excludeProcedures={true}
                        />
                    </Field>
                )}
            </div>

            {/* Info box */}
            <div className="bg-warning/10 border border-warning/30 rounded-[12px] p-[16px]">
                <p className="text-[13px] text-warning flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    <strong>{t('procedures:photosHorodateesDebitImmediatDesJetons')}</strong>
                </p>
                <p className="text-[12px] text-warning/80 mt-[4px] ml-6">
                    {t('procedures:lesJetonsPourLesPhotosHorodateesSerontDebites')}
                </p>
            </div>

            {/* Footer - bouton fixe en bas */}
            <div className="mt-[24px]">
                <Button
                    variant="soft"
                    color="secondary"
                    size="cta"
                    type="button"
                    onClick={handleSubmit}
                    disabled={!canSubmit || isCreatingDraft}
                    className={cn(
                        'w-full rounded-[12px] py-[16px] font-["Poppins",_sans-serif] text-[16px] font-medium transition-colors flex items-center justify-center gap-[8px] h-auto',
                        canSubmit && !isCreatingDraft
                            ? 'bg-secondary text-secondary-foreground hover:bg-secondary-hover'
                            : 'bg-surface-active text-muted-foreground cursor-not-allowed',
                    )}
                >
                    {isCreatingDraft ? (
                        <>
                            <Loader2 className="h-5 w-5 animate-spin" />
                            {t('procedures:preparationDuRapport')}
                        </>
                    ) : (
                        <>
                            {t('procedures:suivant')}
                            <ChevronRight className="h-5 w-5" />
                        </>
                    )}
                </Button>
            </div>
        </div>
    );
}
